import React from 'react'
import { useNavigate } from 'react-router-dom'
import { CaretRight } from '@phosphor-icons/react'
import { formatNameWithPossessiveSuffix, Abacus } from '@/utils/abacus'
import type { Group } from '@/types/database.types'

interface ActiveGroupsWidgetProps {
  groups: Group[]
}

export function ActiveGroupsWidget({ groups }: ActiveGroupsWidgetProps) {
  const navigate = useNavigate()

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between px-1">
        <h3 className="text-[15px] font-bold uppercase tracking-[0.18em] text-espresso/80">
          Aktif Günler
        </h3>
        <button
          className="ai-icon-wrapper flex items-center gap-2.5 bg-espresso/[0.04] text-espresso/80 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.1em] hover:bg-espresso/[0.08] transition-colors border border-espresso/10"
          onClick={() => navigate('/groups')}
        >
          Tümünü Gör
          <div className="w-[1px] h-3 bg-espresso/15" />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            overflow="visible"
            className="animated-iconoir"
            role="img"
            aria-label="credit-cards"
          >
            <path
              d="M22 11.4286V18C22 19.1046 21.1046 20 20 20H7C5.89543 20 5 19.1046 5 18V16.5M22 11.4286V10C22 8.89543 21.1046 8 20 8H19M22 11.4286H19"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="ai-anim-fill ai-delay-0"
              style={{
                fill: 'currentColor',
                fillOpacity: 0,
                transition: 'fill-opacity 500ms ease',
              }}
            />
            <path
              d="M19 8V14.5C19 15.6046 18.1046 16.5 17 16.5H4C2.89543 16.5 2 15.6046 2 14.5V6.5C2 5.39543 2.89543 4.5 4 4.5H17C18.1046 4.5 19 5.39543 19 6.5V8ZM19 8H5.5"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="ai-anim-fade ai-delay-1"
            />
          </svg>
        </button>
      </div>

      <div className="space-y-3 relative z-10 px-1">
        {/* Render active groups dynamically. Filter out completed ones and sort by closest payment date. */}
        {(() => {
          const activeAndSortedGroups = groups
            .filter((g: any) => {
              const cycle = g.current_cycle || 1
              const total = g.total_cycles || g.member_count || 1
              return g.status === 'active' && cycle <= total
            })
            .map((g: any) => {
              const startDate = new Date(g.start_date)
              startDate.setHours(0, 0, 0, 0)
              const cycleForCalc = (g.current_cycle || 0) === 0 ? 1 : g.current_cycle || 1
              const cyclePeriod = g.cycle_period || 'monthly'
              const nextDate = Abacus.calculateNextPaymentDate(
                startDate,
                cycleForCalc - 1,
                cyclePeriod
              )
              return { ...g, _nextDate: nextDate.getTime() }
            })
            .sort((a: any, b: any) => {
              const today = new Date().setHours(0, 0, 0, 0)
              const diffA = a._nextDate - today
              const diffB = b._nextDate - today

              const isAPast = diffA < 0
              const isBPast = diffB < 0

              // Gelecek tarihler her zaman üstte yer almalı
              if (!isAPast && isBPast) return -1
              if (isAPast && !isBPast) return 1

              // Eğer her ikisi de gelecekteyse, en yakın tarihli olan en üstte
              if (!isAPast && !isBPast) return diffA - diffB

              // Eğer her ikisi de geçmişteyse, bugüne en yakın olan (en az geciken) daha üstte
              return diffB - diffA
            })

          if (activeAndSortedGroups.length === 0) {
            return (
              <div className="bg-white p-5 rounded-[24px] border border-espresso/5 shadow-sm text-center text-[14px] text-espresso/50 font-medium">
                Henüz aktif bir grubunuz bulunmuyor.
              </div>
            )
          }

          return activeAndSortedGroups.map(group => {
            const totalCycles = (group as any).total_cycles || 10
            const currentCycle = group.current_cycle || 1
            // Kullanıcı şu an N. döngüdeyse, N-1 tanesi tamamlanmıştır demektir.
            const completedCycles = Math.max(0, currentCycle - 1)
            const progressPct = Math.min((completedCycles / totalCycles) * 100, 100)
            const radius = 22
            const circumference = 2 * Math.PI * radius
            const strokeDashoffset = circumference - (circumference * progressPct) / 100

            const nextMember = (group as any).next_member

            // Get full name for display
            let nextName = 'Sıra Bekleniyor'

            if (nextMember?.full_name) {
              nextName = nextMember.full_name
            }

            return (
              <div
                key={group.id}
                className="bg-white p-4 rounded-[24px] flex items-center justify-between shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-[#F0F0F0] cursor-pointer z-20 relative group/card"
                onClick={() => navigate(`/groups/active?id=${group.id}`)}
              >
                <div className="flex items-center gap-5">
                  <div className="relative w-[50px] h-[50px] shrink-0">
                    <svg className="w-full h-full -rotate-90">
                      {/* Background light grey ring */}
                      <circle
                        className="text-[#F5F5F3]"
                        cx="25"
                        cy="25"
                        fill="transparent"
                        r={radius}
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      {/* Foreground colored ring */}
                      <circle
                        cx="25"
                        cy="25"
                        fill="transparent"
                        r={radius}
                        stroke="#D4AF37"
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        strokeLinecap="round"
                        strokeWidth="4"
                        className="transition-all duration-1000 ease-out"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-[11.5px] font-bold text-[#1A1A1A]">
                        {completedCycles}/{totalCycles}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <h4 className="font-bold text-[16px] leading-tight text-[#1A1A1A]">
                      {group.name}
                    </h4>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="w-[17px] h-[17px] text-[#9CA3AF] shrink-0"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
                        />
                      </svg>
                      <p className="text-[11px] text-[#9CA3AF] font-medium leading-none">
                        <span className="font-bold text-[#374151]">
                          {nextName === 'Sıra Bekleniyor'
                            ? nextName
                            : formatNameWithPossessiveSuffix(nextName)}
                        </span>
                        {nextName !== 'Sıra Bekleniyor' && ' sırası'}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Right Arrow Chevron inside subtle circle */}
                <div className="w-9 h-9 rounded-full bg-[#FDF8E8] flex items-center justify-center transition-transform group-hover/card:translate-x-1 shrink-0 ml-2">
                  <CaretRight size={18} className="text-[#CFA02E]" weight="bold" />
                </div>
              </div>
            )
          })
        })()}
      </div>
    </section>
  )
}
